import { CaptureAnnotation } from "./CaptureRequest";

// Drawing + export for the capture annotation editor. Shapes live in IMAGE
// pixel coordinates (of the screenshot the host sent), so the on-screen view
// (fit / pinch-zoom) and the export share one scene; only the transform
// differs.

export type CaptureEditorTool = "pen" | "arrow" | "rect" | "text" | "blur" | "crop";

export interface EditorPoint {
  x: number;
  y: number;
}

export interface EditorRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type EditorShape =
  | { type: "pen"; color: string; width: number; points: EditorPoint[] }
  | {
      type: "arrow";
      color: string;
      width: number;
      from: EditorPoint;
      to: EditorPoint;
    }
  | { type: "rect"; color: string; width: number; rect: EditorRect }
  | {
      type: "text";
      color: string;
      fontSize: number;
      at: EditorPoint;
      text: string;
    }
  | { type: "blur"; block: number; rect: EditorRect };

export interface EditorDocument {
  shapes: EditorShape[];
  /** The part of the image that will be sent (image pixels). */
  crop: EditorRect;
}

export interface ImageSize {
  width: number;
  height: number;
}

// Six marker colours, red first (the default): readable on light and dark UIs.
export const CAPTURE_EDITOR_COLORS: { value: string; name: string }[] = [
  { value: "#EB144C", name: "Red" },
  { value: "#FF6705", name: "Orange" },
  { value: "#FDB903", name: "Yellow" },
  { value: "#00D082", name: "Green" },
  { value: "#0A93E4", name: "Blue" },
  { value: "#111111", name: "Black" },
];

export const CAPTURE_EXPORT_MAX_EDGE = 2560;
export const CAPTURE_EXPORT_QUALITY = 0.85;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const longEdge = (size: ImageSize) => Math.max(size.width, size.height);

/** Marker stroke width in image pixels — proportional, so it reads the same on a phone and a 4K screenshot. */
export const strokeWidthFor = (size: ImageSize) =>
  Math.max(3, Math.round(longEdge(size) * 0.004));

export const fontSizeFor = (size: ImageSize) =>
  clamp(Math.round(longEdge(size) * 0.026), 18, 96);

/** Pixelation block for blur regions: coarse enough that text can't be read back. */
export const blurBlockFor = (size: ImageSize) =>
  clamp(Math.round(longEdge(size) * 0.012), 10, 40);

export const fullImageRect = (size: ImageSize): EditorRect => ({
  x: 0,
  y: 0,
  w: size.width,
  h: size.height,
});

export const rectFromPoints = (a: EditorPoint, b: EditorPoint): EditorRect => ({
  x: Math.min(a.x, b.x),
  y: Math.min(a.y, b.y),
  w: Math.abs(b.x - a.x),
  h: Math.abs(b.y - a.y),
});

export const clampPointToRect = (
  point: EditorPoint,
  rect: EditorRect,
): EditorPoint => ({
  x: clamp(point.x, rect.x, rect.x + rect.w),
  y: clamp(point.y, rect.y, rect.y + rect.h),
});

export const intersectRects = (
  a: EditorRect,
  b: EditorRect,
): EditorRect | undefined => {
  const x = Math.max(a.x, b.x);
  const y = Math.max(a.y, b.y);
  const right = Math.min(a.x + a.w, b.x + b.w);
  const bottom = Math.min(a.y + a.h, b.y + b.h);
  if (right <= x || bottom <= y) {
    return undefined;
  }
  return { x, y, w: right - x, h: bottom - y };
};

export const textFont = (fontSize: number) =>
  `600 ${Math.round(fontSize)}px system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;

export const TEXT_LINE_HEIGHT = 1.2;

let measureContext: CanvasRenderingContext2D | null | undefined;
export const measureTextWidth = (text: string, fontSize: number): number => {
  try {
    if (measureContext === undefined) {
      measureContext = document.createElement("canvas").getContext("2d");
    }
    if (measureContext) {
      measureContext.font = textFont(fontSize);
      return measureContext.measureText(text).width;
    }
  } catch (_) {}
  return text.length * fontSize * 0.55;
};

const luminance = (hex: string) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000;
};

/** Outline colour that keeps a marker readable on any background. */
export const outlineColorFor = (color: string) =>
  luminance(color) > 150 ? "rgba(17, 17, 17, 0.9)" : "rgba(255, 255, 255, 0.95)";

export const shapeBounds = (shape: EditorShape): EditorRect => {
  switch (shape.type) {
    case "rect":
    case "blur":
      return shape.rect;
    case "arrow":
      return rectFromPoints(shape.from, shape.to);
    case "pen": {
      const xs = shape.points.map((point) => point.x);
      const ys = shape.points.map((point) => point.y);
      const x = Math.min(...xs);
      const y = Math.min(...ys);
      return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
    }
    case "text":
      return {
        x: shape.at.x,
        y: shape.at.y,
        w: measureTextWidth(shape.text, shape.fontSize),
        h: shape.fontSize * TEXT_LINE_HEIGHT,
      };
  }
};

// ── Drawing ─────────────────────────────────────────────────────────────────

const pixelatedPatches = new WeakMap<EditorShape, HTMLCanvasElement>();

const pixelatedPatchFor = (
  shape: Extract<EditorShape, { type: "blur" }>,
  source: CanvasImageSource,
): HTMLCanvasElement | undefined => {
  const cached = pixelatedPatches.get(shape);
  if (cached) {
    return cached;
  }
  const { rect, block } = shape;
  if (rect.w < 1 || rect.h < 1) {
    return undefined;
  }
  const patch = document.createElement("canvas");
  patch.width = Math.max(1, Math.ceil(rect.w / block));
  patch.height = Math.max(1, Math.ceil(rect.h / block));
  const context = patch.getContext("2d");
  if (!context) {
    return undefined;
  }
  // Downscale: every block collapses into one averaged pixel. The detail is
  // gone from here on — the export never sees the original pixels again.
  context.imageSmoothingEnabled = true;
  context.drawImage(
    source,
    rect.x,
    rect.y,
    rect.w,
    rect.h,
    0,
    0,
    patch.width,
    patch.height,
  );
  pixelatedPatches.set(shape, patch);
  return patch;
};

const drawPixelated = (
  context: CanvasRenderingContext2D,
  shape: Extract<EditorShape, { type: "blur" }>,
  source: CanvasImageSource,
) => {
  const patch = pixelatedPatchFor(shape, source);
  if (!patch) {
    return;
  }
  context.save();
  context.imageSmoothingEnabled = false;
  context.drawImage(
    patch,
    0,
    0,
    patch.width,
    patch.height,
    shape.rect.x,
    shape.rect.y,
    shape.rect.w,
    shape.rect.h,
  );
  context.restore();
};

const roundedRectPath = (
  context: CanvasRenderingContext2D,
  rect: EditorRect,
  radius: number,
) => {
  const r = Math.max(0, Math.min(radius, rect.w / 2, rect.h / 2));
  context.beginPath();
  context.moveTo(rect.x + r, rect.y);
  context.lineTo(rect.x + rect.w - r, rect.y);
  context.quadraticCurveTo(rect.x + rect.w, rect.y, rect.x + rect.w, rect.y + r);
  context.lineTo(rect.x + rect.w, rect.y + rect.h - r);
  context.quadraticCurveTo(
    rect.x + rect.w,
    rect.y + rect.h,
    rect.x + rect.w - r,
    rect.y + rect.h,
  );
  context.lineTo(rect.x + r, rect.y + rect.h);
  context.quadraticCurveTo(rect.x, rect.y + rect.h, rect.x, rect.y + rect.h - r);
  context.lineTo(rect.x, rect.y + r);
  context.quadraticCurveTo(rect.x, rect.y, rect.x + r, rect.y);
  context.closePath();
};

const strokeWithHalo = (
  context: CanvasRenderingContext2D,
  color: string,
  width: number,
) => {
  // A soft halo under every marker keeps a red box readable on a red UI.
  context.save();
  context.strokeStyle = outlineColorFor(color);
  context.globalAlpha = 0.55;
  context.lineWidth = width + Math.max(2, width * 0.6);
  context.stroke();
  context.restore();
  context.strokeStyle = color;
  context.lineWidth = width;
  context.stroke();
};

const drawArrow = (
  context: CanvasRenderingContext2D,
  shape: Extract<EditorShape, { type: "arrow" }>,
) => {
  const { from, to, color, width } = shape;
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const length = Math.hypot(to.x - from.x, to.y - from.y);
  const headLength = Math.min(length * 0.6, width * 4.5);
  const headAngle = Math.PI / 7;
  // The shaft stops short of the tip so its square end never pokes through.
  const shaftEnd = {
    x: to.x - Math.cos(angle) * headLength * 0.6,
    y: to.y - Math.sin(angle) * headLength * 0.6,
  };
  context.lineCap = "round";
  context.lineJoin = "round";
  context.beginPath();
  context.moveTo(from.x, from.y);
  context.lineTo(shaftEnd.x, shaftEnd.y);
  strokeWithHalo(context, color, width);

  context.beginPath();
  context.moveTo(to.x, to.y);
  context.lineTo(
    to.x - headLength * Math.cos(angle - headAngle),
    to.y - headLength * Math.sin(angle - headAngle),
  );
  context.lineTo(
    to.x - headLength * Math.cos(angle + headAngle),
    to.y - headLength * Math.sin(angle + headAngle),
  );
  context.closePath();
  context.save();
  context.strokeStyle = outlineColorFor(color);
  context.globalAlpha = 0.55;
  context.lineWidth = Math.max(2, width * 0.6);
  context.stroke();
  context.restore();
  context.fillStyle = color;
  context.fill();
};

const drawPen = (
  context: CanvasRenderingContext2D,
  shape: Extract<EditorShape, { type: "pen" }>,
) => {
  const { points, color, width } = shape;
  if (points.length === 0) {
    return;
  }
  context.lineCap = "round";
  context.lineJoin = "round";
  context.beginPath();
  context.moveTo(points[0].x, points[0].y);
  if (points.length === 1) {
    // A tap: a dot.
    context.lineTo(points[0].x + 0.01, points[0].y + 0.01);
  } else {
    // Midpoint smoothing: quadratic curves through the sampled points.
    for (let i = 1; i < points.length - 1; i++) {
      const mid = {
        x: (points[i].x + points[i + 1].x) / 2,
        y: (points[i].y + points[i + 1].y) / 2,
      };
      context.quadraticCurveTo(points[i].x, points[i].y, mid.x, mid.y);
    }
    const last = points[points.length - 1];
    context.lineTo(last.x, last.y);
  }
  strokeWithHalo(context, color, width);
};

const drawText = (
  context: CanvasRenderingContext2D,
  shape: Extract<EditorShape, { type: "text" }>,
) => {
  context.font = textFont(shape.fontSize);
  context.textBaseline = "top";
  context.textAlign = "left";
  context.lineJoin = "round";
  // Vertically centre the glyphs in the same line box the text input uses.
  const y = shape.at.y + (shape.fontSize * (TEXT_LINE_HEIGHT - 1)) / 2;
  context.strokeStyle = outlineColorFor(shape.color);
  context.lineWidth = Math.max(3, shape.fontSize * 0.16);
  context.strokeText(shape.text, shape.at.x, y);
  context.fillStyle = shape.color;
  context.fillText(shape.text, shape.at.x, y);
};

export const drawShape = (
  context: CanvasRenderingContext2D,
  shape: EditorShape,
  source: CanvasImageSource,
) => {
  context.save();
  switch (shape.type) {
    case "blur":
      drawPixelated(context, shape, source);
      break;
    case "rect":
      roundedRectPath(context, shape.rect, shape.width * 1.2);
      context.lineJoin = "round";
      strokeWithHalo(context, shape.color, shape.width);
      break;
    case "arrow":
      drawArrow(context, shape);
      break;
    case "pen":
      drawPen(context, shape);
      break;
    case "text":
      drawText(context, shape);
      break;
  }
  context.restore();
};

/**
 * The annotated screenshot in image coordinates: the image, then the blur
 * regions (they hide what's on the screenshot), then the markers on top.
 */
export const drawScene = (
  context: CanvasRenderingContext2D,
  source: CanvasImageSource,
  size: ImageSize,
  shapes: EditorShape[],
) => {
  context.drawImage(source, 0, 0, size.width, size.height);
  shapes
    .filter((shape) => shape.type === "blur")
    .forEach((shape) => drawShape(context, shape, source));
  shapes
    .filter((shape) => shape.type !== "blur")
    .forEach((shape) => drawShape(context, shape, source));
};

// ── Export ──────────────────────────────────────────────────────────────────

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const fromDataUrl = () => {
      try {
        const dataUrl = canvas.toDataURL(type, quality);
        const [header, base64] = dataUrl.split(",");
        const mime = /data:([^;]+)/.exec(header)?.[1] ?? type;
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        resolve(new Blob([bytes], { type: mime }));
      } catch (error) {
        reject(error);
      }
    };
    if (typeof canvas.toBlob !== "function") {
      fromDataUrl();
      return;
    }
    try {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : fromDataUrl()),
        type,
        quality,
      );
    } catch (error) {
      reject(error);
    }
  });

const round4 = (value: number) => Math.round(value * 10000) / 10000;

/**
 * The contract's annotation list (§2): normalized 0..1 against the exported
 * (cropped) image. Shapes entirely outside the crop are dropped, others are
 * clipped to it. Arrows keep their direction: tail in x/y, vector in w/h.
 */
export const toCaptureAnnotations = (
  doc: EditorDocument,
): CaptureAnnotation[] => {
  const { crop } = doc;
  const annotations: CaptureAnnotation[] = [];
  doc.shapes.forEach((shape) => {
    if (shape.type === "arrow") {
      const from = clampPointToRect(shape.from, crop);
      const to = clampPointToRect(shape.to, crop);
      if (from.x === to.x && from.y === to.y) {
        return;
      }
      annotations.push({
        type: "arrow",
        x: round4((from.x - crop.x) / crop.w),
        y: round4((from.y - crop.y) / crop.h),
        w: round4((to.x - from.x) / crop.w),
        h: round4((to.y - from.y) / crop.h),
        color: shape.color,
      });
      return;
    }
    const visible = intersectRects(shapeBounds(shape), crop);
    if (!visible) {
      return;
    }
    annotations.push({
      type: shape.type,
      x: round4((visible.x - crop.x) / crop.w),
      y: round4((visible.y - crop.y) / crop.h),
      w: round4(visible.w / crop.w),
      h: round4(visible.h / crop.h),
      ...(shape.type === "text" ? { text: shape.text.slice(0, 500) } : {}),
      ...(shape.type !== "blur" ? { color: shape.color } : {}),
    });
  });
  return annotations;
};

export interface CaptureExport {
  blob: Blob;
  width: number;
  height: number;
  annotations: CaptureAnnotation[];
}

/**
 * Flattens the annotated screenshot into a JPEG (long edge ≤ 2560 px, q≈0.85).
 * Blur regions are pixelated INTO the exported pixels — the original content
 * under them never leaves the device.
 */
export const exportAnnotatedCapture = async (
  source: CanvasImageSource,
  size: ImageSize,
  doc: EditorDocument,
): Promise<CaptureExport> => {
  const crop =
    intersectRects(doc.crop, fullImageRect(size)) ?? fullImageRect(size);
  const scale = Math.min(1, CAPTURE_EXPORT_MAX_EDGE / Math.max(crop.w, crop.h));
  const width = Math.max(1, Math.round(crop.w * scale));
  const height = Math.max(1, Math.round(crop.h * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas unavailable");
  }
  // JPEG has no alpha: transparent screenshot areas become white, not black.
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.setTransform(scale, 0, 0, scale, -crop.x * scale, -crop.y * scale);
  drawScene(context, source, size, doc.shapes);
  const blob = await canvasToBlob(canvas, "image/jpeg", CAPTURE_EXPORT_QUALITY);
  return {
    blob,
    width,
    height,
    annotations: toCaptureAnnotations({ ...doc, crop }),
  };
};
